<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import type { Store } from '../state/store.svelte';
  import type { Renderer, DragOverlay } from '../render/renderer';
  import type { GraphicsProfile, BodyId } from '../state/selectors';
  import {
    selectBodyView,
    selectBodyWorldPosition,
    selectEffectiveMeanAnomaly,
    selectMeanAnomalyAtWorldPoint,
    selectOutermostApoapsisWorldPoint,
    selectOutermostOrbitVerticalExtremes
  } from '../state/selectors';
  import { ZOOM_STEP } from '../state/camera';
  import { describeCentreSwitch } from './centre-switch';
  import type { ResolvedProfile } from '../render/resolve-profile';
  import { ENHANCED_FEATURES } from '../render/resolve-profile';
  import { describeSystem } from './system-summary';

  // Full-bleed canvas host. The Canvas2D BASELINE always owns `sim-canvas` and
  // draws the complete geometry (AD-3: Canvas2D is the complete baseline), so
  // `getContext('2d')` on it is always valid and simulation correctness is never
  // at risk. The Enhanced (WebGL) adapter is a DECORATION overlay on a second,
  // transparent, pointer-events:none canvas layered above; it adds only degradable
  // decoration (AD-9) and never replaces the baseline. This keeps a WebGL
  // context-loss or construction failure trivially safe: drop the overlay, the
  // baseline is untouched, and all state (orbital params, time, playback,
  // selection) is preserved because it lives in the store, not the adapter.
  //
  // `resolve` is a capability-bound resolver passed from the composition root, so
  // `ui` never imports `platform`. `announce` surfaces a fallback as a calm,
  // non-blocking live-region notice (never a thrown error or modal).
  let {
    store,
    resolve,
    osReducedMotion = false,
    announce
  }: {
    store: Store;
    resolve: (profile: GraphicsProfile) => ResolvedProfile;
    osReducedMotion?: boolean;
    announce?: (message: string) => void;
  } = $props();

  let canvasEl: HTMLCanvasElement;
  let decorEl: HTMLCanvasElement;
  let baseline: Renderer | undefined;
  let overlay: Renderer | undefined;

  const summary = $derived(describeSystem(store.state));

  // --- Direct-manipulation phase drag (Story 1.7, FR-19) -----------------------
  // The drag is EPHEMERAL ui interaction, never simulation state (AD-2): it lives
  // here and is surfaced to the renderer as a transient overlay argument. Pointer
  // maps to a world point through the ONE shared projection owned by `render`
  // (AD-10) via `baseline.getProjection()`; the ui computes no transform itself.
  // A grab hit radius comfortable for imprecise pointing (AD-8), in CSS pixels.
  const GRAB_HIT_RADIUS_CSS = 26;
  // A drop-on-centre hit radius in CSS pixels.
  const CENTRE_HIT_RADIUS_CSS = 34;

  interface ActiveDrag {
    readonly bodyId: BodyId;
    /** The play/pause state before the drag, restored on release (AC 3). */
    readonly wasPlaying: boolean;
  }
  let activeDrag: ActiveDrag | null = null;
  let dragOverlay = $state<DragOverlay | null>(null);

  // --- View pan (Story 3.2) ----------------------------------------------------
  // A pan is DISTINCT from and mutually exclusive with the 1.7 phase drag: it
  // starts only on EMPTY canvas space (hitTestBody === null), is VIEW-ONLY (never
  // pauses the sim, never selects a body, never touches play/pause), and moves the
  // viewport by dispatching `PanView` world deltas computed through the ONE shared
  // projection inverse (AD-10). Because that inverse is affine and pan is additive,
  // the per-move delta is correct at any current zoom/pan.
  let activePan = $state<{ lastX: number; lastY: number } | null>(null);

  // World (SI metres) -> client CSS coordinates, through the shared projection.
  function worldToClient(wx: number, wy: number): { x: number; y: number } | null {
    const projection = baseline?.getProjection();
    if (!projection) return null;
    const rect = canvasEl.getBoundingClientRect();
    const dpr = projection.viewport.dpr;
    const s = projection.worldToScreen(wx, wy);
    return { x: rect.left + s.x / dpr, y: rect.top + s.y / dpr };
  }

  // Client CSS pointer -> focus-centred SI world point, through the shared
  // projection inverse (AD-10). The same dpr is used both ways so hit-test and
  // inverse agree exactly.
  function clientToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
    const projection = baseline?.getProjection();
    if (!projection) return null;
    const rect = canvasEl.getBoundingClientRect();
    const dpr = projection.viewport.dpr;
    const px = (clientX - rect.left) * dpr;
    const py = (clientY - rect.top) * dpr;
    return projection.screenToWorld(px, py);
  }

  // The nearest active, non-central body within the grab radius of the pointer,
  // or null. The central body is never draggable (AC 8).
  function hitTestBody(clientX: number, clientY: number): BodyId | null {
    let best: BodyId | null = null;
    let bestDist = GRAB_HIT_RADIUS_CSS;
    for (const b of store.state.bodies) {
      if (b.id === store.state.centralBody || b.status !== 'active') continue;
      const world = selectBodyWorldPosition(store.state, b.id);
      const client = worldToClient(world.x, world.y);
      if (!client) continue;
      const d = Math.hypot(client.x - clientX, client.y - clientY);
      if (d <= bestDist) {
        bestDist = d;
        best = b.id;
      }
    }
    return best;
  }

  // Whether the pointer is over the current centre marker (drop-on-centre target).
  function pointerOverCentre(clientX: number, clientY: number): boolean {
    const client = worldToClient(0, 0);
    if (!client) return false;
    return Math.hypot(client.x - clientX, client.y - clientY) <= CENTRE_HIT_RADIUS_CSS;
  }

  // Shortest signed angle from `from` to `to`, in (-pi, pi], so the body follows
  // the pointer along the short arc of its ellipse.
  function shortestAngle(from: number, to: number): number {
    return Math.atan2(Math.sin(to - from), Math.cos(to - from));
  }

  function handlePointerDown(event: PointerEvent): void {
    if (activeDrag || activePan) return;
    if (!baseline?.getProjection()) return;
    const bodyId = hitTestBody(event.clientX, event.clientY);
    if (!bodyId) {
      // Empty space (incl. over the centre with no body under the pointer): begin a
      // VIEW pan (Story 3.2 arbitration). Not a phase drag: no Pause, no SelectBody,
      // no play/pause change. Focus the canvas so the arrow-key pan works after the
      // pointer interaction (preventDefault would otherwise suppress focus).
      event.preventDefault();
      try {
        canvasEl.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is best-effort; a failure never blocks the pan.
      }
      canvasEl.focus();
      activePan = { lastX: event.clientX, lastY: event.clientY };
      return;
    }
    event.preventDefault();
    try {
      canvasEl.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is best-effort; a failure never blocks the drag.
    }
    // Select the grabbed body (matches the Story 1.3 selection model) so the
    // detail panel reflects it and the selection ring tracks the drag.
    store.dispatch({ type: 'SelectBody', body: bodyId });
    // Pause motion for the drag; dragging SETS phase, it does not run time forward
    // (AC 3). The prior play/pause state is restored on release. Dispatched here,
    // never writing the playback cookie (that is the HUD's job), so the persisted
    // preference is untouched.
    const wasPlaying = !store.state.paused;
    if (wasPlaying) store.dispatch({ type: 'Pause' });
    activeDrag = { bodyId, wasPlaying };
    dragOverlay = { bodyId, overCentre: pointerOverCentre(event.clientX, event.clientY) };
  }

  function handlePointerMove(event: PointerEvent): void {
    if (activePan) {
      // Map the previous and current pointer through the ONE shared inverse and
      // pan by their world delta so the viewport follows the pointer. The delta is
      // offset-independent (affine + additive), so a lagging projection is fine.
      const worldPrev = clientToWorld(activePan.lastX, activePan.lastY);
      const worldCurr = clientToWorld(event.clientX, event.clientY);
      if (worldPrev && worldCurr) {
        store.dispatch({
          type: 'PanView',
          dxWorld: worldPrev.x - worldCurr.x,
          dyWorld: worldPrev.y - worldCurr.y
        });
      }
      activePan = { lastX: event.clientX, lastY: event.clientY };
      return;
    }
    if (!activeDrag) return;
    const world = clientToWorld(event.clientX, event.clientY);
    if (world) {
      const targetM = selectMeanAnomalyAtWorldPoint(
        store.state,
        activeDrag.bodyId,
        world.x,
        world.y
      );
      const currentM = selectEffectiveMeanAnomaly(store.state, activeDrag.bodyId);
      const delta = shortestAngle(currentM, targetM);
      if (delta !== 0) {
        store.dispatch({ type: 'NudgePhase', body: activeDrag.bodyId, deltaMeanAnomaly: delta });
      }
    }
    dragOverlay = {
      bodyId: activeDrag.bodyId,
      overCentre: pointerOverCentre(event.clientX, event.clientY)
    };
  }

  function endDrag(event: PointerEvent, allowSwitch: boolean): void {
    if (!activeDrag) return;
    const drag = activeDrag;
    activeDrag = null;
    dragOverlay = null;
    try {
      canvasEl.releasePointerCapture(event.pointerId);
    } catch {
      // Ignore: capture may already be gone (e.g. after pointercancel).
    }
    // Drop-on-centre: raise the SAME `SwitchCentre` command as the "Make centre"
    // button and the central-body control (Story 2.2, AC 3, so the three entry
    // points cannot diverge). Capture the previous-centre name BEFORE dispatch and
    // announce the real success through the live region (AC 9). An off-centre drop
    // performs no switch (the guard below) and the body simply stays on its orbit.
    // The dragged body is always active and non-central (hitTestBody excludes the
    // centre), so this always changes the centre; the reducer no longer throws.
    if (allowSwitch && pointerOverCentre(event.clientX, event.clientY)) {
      const prevName = selectBodyView(store.state, store.state.centralBody).name;
      const newName = selectBodyView(store.state, drag.bodyId).name;
      store.dispatch({ type: 'SwitchCentre', body: drag.bodyId });
      announce?.(describeCentreSwitch(prevName, newName));
    }
    // Restore the pre-drag play/pause state (AC 3). Only re-play if it was playing.
    if (drag.wasPlaying) store.dispatch({ type: 'Play' });
  }

  // End a view pan (Story 3.2). View-only: releases capture and clears the pan
  // state; touches no simulation or play/pause state. Returns true if a pan ended.
  function endPan(event: PointerEvent): boolean {
    if (!activePan) return false;
    activePan = null;
    try {
      canvasEl.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may already be gone (e.g. after pointercancel).
    }
    return true;
  }

  function handlePointerUp(event: PointerEvent): void {
    if (endPan(event)) return;
    endDrag(event, true);
  }

  function handlePointerCancel(event: PointerEvent): void {
    if (endPan(event)) return;
    // Treat cancellation as a release with no switch: restore play/pause safely.
    endDrag(event, false);
  }

  // Wheel zoom about the cursor focus (Story 3.2). Continuous gesture: silent (no
  // per-notch announcement; AD-8 announces MAJOR discrete changes only). `deltaY <
  // 0` (scroll up) zooms in (factor > 1). The focus is the world point under the
  // cursor, mapped through the shared inverse, so it stays under the cursor.
  function handleWheel(event: WheelEvent): void {
    if (activeDrag) return;
    if (!baseline?.getProjection()) return;
    event.preventDefault();
    const factor = Math.exp(-event.deltaY * 0.0015);
    const focus = clientToWorld(event.clientX, event.clientY);
    if (focus) {
      store.dispatch({ type: 'ZoomView', factor, focusX: focus.x, focusY: focus.y });
    } else {
      store.dispatch({ type: 'ZoomView', factor });
    }
  }

  // Canvas keyboard pan/zoom (Story 3.2, AC-2; the binding 1.7 reserved). Arrow
  // keys pan (PanStep), `+`/`=` zoom in, `-` zoom out. Phase stepping is NOT handled
  // here — Story 1.7 scoped its phase arrows to the detail-panel stepper on purpose.
  function handleKeydown(event: KeyboardEvent): void {
    if (activeDrag || activePan) return;
    switch (event.key) {
      case 'ArrowLeft':
        store.dispatch({ type: 'PanStep', dir: 'left' });
        break;
      case 'ArrowRight':
        store.dispatch({ type: 'PanStep', dir: 'right' });
        break;
      case 'ArrowUp':
        store.dispatch({ type: 'PanStep', dir: 'up' });
        break;
      case 'ArrowDown':
        store.dispatch({ type: 'PanStep', dir: 'down' });
        break;
      case '+':
      case '=':
        store.dispatch({ type: 'ZoomView', factor: ZOOM_STEP });
        break;
      case '-':
      case '_':
        store.dispatch({ type: 'ZoomView', factor: 1 / ZOOM_STEP });
        break;
      default:
        return; // unhandled key: let it bubble (no scroll suppression)
    }
    event.preventDefault();
  }

  function applySize(): void {
    const rect = canvasEl.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    baseline?.resize(rect.width, rect.height, dpr);
    baseline?.render(store.state, dragOverlay);
    overlay?.resize(rect.width, rect.height, dpr);
    overlay?.render(store.state);
  }

  function tearDownOverlay(): void {
    overlay?.destroy();
    overlay = undefined;
  }

  // Record the concrete adapter that actually mounted (never claim Enhanced when it
  // is not live, AC 5). Idempotent: the reducer returns the same reference when
  // unchanged, so this never churns the store or re-triggers the rebuild effect.
  function recordActiveMode(mode: 'enhanced' | 'compatibility', features: readonly string[]): void {
    store.dispatch({ type: 'SetActiveRenderMode', mode, unavailableFeatures: features });
  }

  // Safe fallback to the Canvas2D baseline (context loss or failed Enhanced
  // construction). The baseline keeps drawing; only decoration is dropped (AD-9).
  function fallbackToCompatibility(reason: string): void {
    tearDownOverlay();
    recordActiveMode('compatibility', ENHANCED_FEATURES);
    announce?.(reason);
    baseline?.render(store.state, dragOverlay);
  }

  // Reconcile the decoration overlay with the requested profile. Called once on
  // mount and again whenever `requestedProfile` changes. Building or losing the
  // overlay never touches the baseline or any simulated state.
  function reconcileOverlay(profile: GraphicsProfile): void {
    const resolved = resolve(profile);
    tearDownOverlay();

    if (resolved.activeMode !== 'enhanced') {
      // Compatibility (requested, or an Enhanced request with no WebGL): baseline
      // only. Report the device-unavailable enhanced features for the diagnostics.
      recordActiveMode('compatibility', resolved.unavailableFeatures);
      return;
    }

    // Enhanced: try to mount the WebGL decoration overlay. A construction failure
    // falls back safely to the baseline (AC 4).
    try {
      overlay = resolved.createRenderer(decorEl, {
        onContextLost: () =>
          fallbackToCompatibility(
            'Enhanced graphics were lost; switched to Compatibility mode. Every control and reading is unchanged.'
          ),
        reducedMotion: osReducedMotion
      });
      const rect = canvasEl.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      overlay.resize(rect.width, rect.height, dpr);
      overlay.render(store.state);
      recordActiveMode('enhanced', []);
    } catch {
      fallbackToCompatibility(
        'Enhanced graphics are unavailable; running in Compatibility mode. Every control and reading is unchanged.'
      );
    }
  }

  onMount(() => {
    // Baseline is always the Canvas2D adapter (resolve('compatibility') always
    // yields it), bound to `sim-canvas`. The OS reduced-motion signal is threaded
    // in the SAME way the WebGL overlay receives it, so reduced-effects gates
    // decoration in Compatibility mode too (AC6 / Task 8); the in-app toggle is
    // read live from state on each render.
    baseline = resolve('compatibility').createRenderer(canvasEl, {
      reducedMotion: osReducedMotion
    });

    const raf = requestAnimationFrame(() => {
      applySize();
      reconcileOverlay(store.state.requestedProfile);
    });

    const observer = new ResizeObserver(() => applySize());
    observer.observe(canvasEl);

    // Read-only coordinate seam for Playwright, gated behind a flag the e2e sets
    // via addInitScript so the production build ships nothing. It exposes ONLY
    // read helpers (no store mutation): the client-space position of a body or the
    // centre, mapped through the shared projection (AD-10), so the test can drive
    // real page.mouse pointer input at the true on-screen location.
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { __KEPLER_EXPOSE_TEST__?: boolean }).__KEPLER_EXPOSE_TEST__ === true
    ) {
      (window as unknown as { __keplerCanvasProbe__?: unknown }).__keplerCanvasProbe__ = {
        bodyClient: (id: BodyId) => {
          const world = selectBodyWorldPosition(store.state, id);
          return worldToClient(world.x, world.y);
        },
        centreClient: () => worldToClient(0, 0),
        // Story 3.2 camera accessors. All read through the SAME shared projection
        // the app renders with (AD-10), so the e2e sees the true on-screen effect
        // of zoom/pan. `worldClient` maps any world point; `outermostApoapsisClient`
        // gives the point Fit-all must frame (AC-3); `camera` exposes the live view.
        worldClient: (wx: number, wy: number) => worldToClient(wx, wy),
        outermostApoapsisClient: () => {
          const world = selectOutermostApoapsisWorldPoint(store.state);
          return world ? worldToClient(world.x, world.y) : null;
        },
        // Top/bottom of the outermost orbit (off the midline) — the points that
        // actually test the reserved chrome inset at fit (AC-5).
        outermostOrbitExtremesClient: () => {
          const w = selectOutermostOrbitVerticalExtremes(store.state);
          if (!w) return null;
          const top = worldToClient(w.top.x, w.top.y);
          const bottom = worldToClient(w.bottom.x, w.bottom.y);
          return top && bottom ? { top, bottom } : null;
        },
        camera: () => {
          const projection = baseline?.getProjection();
          return {
            zoom: store.state.camera.zoom,
            panX: store.state.camera.panX,
            panY: store.state.camera.panY,
            metresPerCssPixel: projection?.camera.metresPerCssPixel ?? null
          };
        }
      };
    }

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      tearDownOverlay();
      baseline?.destroy();
      baseline = undefined;
    };
  });

  // Re-render the baseline (and overlay if present) on any state change. This runs
  // for every state change including SetActiveRenderMode; rendering is idempotent
  // and dispatches nothing, so there is no loop.
  $effect(() => {
    void store.state;
    // Also re-render when the transient drag overlay changes (grab/drop cue).
    const currentOverlay = dragOverlay;
    baseline?.render(store.state, currentOverlay);
    overlay?.render(store.state);
  });

  // --- Paused-decoration render loop (Story 6.1) --------------------------------
  // The canvas only repaints on a state change (the $effect above) and the sim clock
  // ticks only while playing (animation-loop.ts). So while PAUSED there is no
  // repaint and the marching-dashes selection ring would freeze. This render-only
  // rAF loop repaints WHILE paused so `lineDashOffset` (a wall-clock read in the
  // renderer) keeps advancing, WITHOUT ever dispatching or ticking, so `state.time`
  // is invariant (AC-2). During PLAY the sim tick already repaints every frame, so
  // this loop deliberately does not run then.
  //
  // The gate conditions are read SYNCHRONOUSLY here so they are tracked and the
  // effect restarts/stops when they change; `store.state`/`dragOverlay` are read
  // only INSIDE the rAF callback (untracked), so the loop's renders cannot mutate
  // state and cannot retrigger this effect (no infinite loop). The loop runs only
  // when paused, motion is allowed (neither the OS reduced-motion signal nor the
  // in-app reduced-effects toggle), and a spinnable body is selected — the SAME
  // condition the renderer draws the ring on (non-central, present, active). Under
  // reduced motion or with nothing to spin it never runs (AC-3/AC-4), so a paused
  // canvas with no reason to move stays static.
  $effect(() => {
    const state = store.state;
    const reducedMotion = osReducedMotion || state.reducedEffects;
    const selected = state.bodies.find((b) => b.id === state.selectedBody);
    const spinnableSelection =
      state.selectedBody !== state.centralBody &&
      selected !== undefined &&
      selected.status === 'active';
    if (!state.paused || reducedMotion || !spinnableSelection) return;

    // Throttle the render-only repaint to ~30fps. The dashes advance ~one dash cell
    // every 0.7s, so 30fps is visibly smooth; capping the frequency halves the
    // main-thread cost of an unconditional 60fps full-scene redraw while paused (the
    // "~60fps" Dev Note describes the mechanism, not an AC — AC-1 only requires
    // continuous marching, which this preserves). `lineDashOffset` stays time-derived
    // in the renderer, so the throttle changes only smoothness, never dash speed or
    // direction. During PLAY the sim tick still repaints at full frame rate for free.
    const MIN_INTERVAL_MS = 1000 / 30;
    let lastPaint = 0;
    let frame = requestAnimationFrame(function tick(nowMs: number) {
      if (nowMs - lastPaint >= MIN_INTERVAL_MS) {
        lastPaint = nowMs;
        // Render-only: never dispatch/tick, so state.time is invariant (AC-2). Reads
        // of store.state/dragOverlay here are untracked (outside the effect body).
        baseline?.render(store.state, dragOverlay);
        overlay?.render(store.state);
      }
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  });

  // Rebuild the overlay ONLY when the requested profile changes. Narrow tracked
  // read (requestedProfile only) so a SetActiveRenderMode dispatch does not
  // re-trigger this effect. Skip the initial run (the overlay is built once in
  // onMount, after the baseline and canvases exist) to avoid a double build.
  let firstRebuildRun = true;
  $effect(() => {
    const profile = store.state.requestedProfile;
    if (firstRebuildRun) {
      firstRebuildRun = false;
      return;
    }
    untrack(() => {
      if (baseline) reconcileOverlay(profile);
    });
  });
</script>

<div class="canvas-host">
  <!-- Baseline Canvas2D surface (always 2D). The hidden paragraph mirrors the
       accessible summary for assistive technology (AD-8). -->
  <canvas
    bind:this={canvasEl}
    class="sim-canvas"
    class:dragging={dragOverlay !== null}
    tabindex="0"
    aria-label={summary}
    data-testid="sim-canvas"
    data-dragging={dragOverlay !== null}
    data-drag-over-centre={dragOverlay?.overCentre === true}
    data-panning={activePan !== null}
    onpointerdown={handlePointerDown}
    onpointermove={handlePointerMove}
    onpointerup={handlePointerUp}
    onpointercancel={handlePointerCancel}
    onwheel={handleWheel}
    onkeydown={handleKeydown}
  ></canvas>
  <!-- Enhanced WebGL decoration overlay: transparent, non-interactive, decoration
       only (AD-3 / AD-9). Never carries the geometry or any state cue. -->
  <canvas bind:this={decorEl} class="decor-canvas" aria-hidden="true" data-testid="decor-canvas"
  ></canvas>
  <p class="visually-hidden" data-testid="canvas-summary">{summary}</p>
</div>

<style>
  .canvas-host {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  .sim-canvas {
    display: block;
    width: 100%;
    height: 100%;
    background: var(--surface-container-lowest);
    touch-action: none;
  }

  .sim-canvas.dragging {
    cursor: grabbing;
  }

  .decor-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: transparent;
  }
</style>
